/**
 * Promise-based confirmation dialog (docs/FRONTEND-PLAN.md §2.1).
 *
 * Replaces bare destructive actions and the native confirm/prompt dialogs:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete post?', message: '…', danger: true })) {
 *     await forumApi.deletePost(postId);
 *   }
 *
 * The provider owns a single <Modal>, so only one dialog can ever be open and
 * every confirm in the app looks identical. Mounted in Layout alongside
 * ToastHost — i.e. it covers the whole authenticated tree.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Modal from '../components/Modal';
import { ui } from './ui';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  danger: false
};

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        // A second confirm while one is open resolves the first as cancelled
        // rather than leaking a pending promise.
        if (resolveRef.current) resolveRef.current(false);
        resolveRef.current = resolve;
        setRequest({ ...DEFAULTS, ...(options || {}) });
      }),
    []
  );

  const settle = useCallback((answer) => {
    setRequest(null);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    if (resolve) resolve(answer);
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={Boolean(request)}
        onClose={() => settle(false)}
        title={request ? request.title : ''}
        footer={
          request && (
            <>
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => settle(false)}
              >
                {request.cancelLabel}
              </button>
              <button
                type="button"
                className={request.danger ? ui.btnDanger : ui.btnPrimary}
                onClick={() => settle(true)}
              >
                {request.confirmLabel}
              </button>
            </>
          )
        }
      >
        {request && request.message ? <p>{request.message}</p> : null}
      </Modal>
    </ConfirmContext.Provider>
  );
}

/** Returns `confirm(options) => Promise<boolean>`. */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx.confirm;
}
